import { useEffect, useMemo, useRef, useState } from 'react';

import { NetworkStatus } from '@apollo/client';
import type { ErrorLike } from '@apollo/client';

type UseDashboardClientListOptions<TItem> = {
  items: TItem[];
  loading: boolean;
  error?: ErrorLike | null;
  limit?: number;
};

export const useDashboardClientList = <TItem>({
  items: allItems,
  loading: isLoading,
  error: initialError,
  limit = 4,
}: UseDashboardClientListOptions<TItem>) => {
  const [displayCount, setDisplayCount] = useState(limit);
  const [error, setError] = useState<ErrorLike | null>(initialError ?? null);
  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setError(initialError ?? null);
  }, [initialError]);

  const items = useMemo(
    () => allItems.slice(0, Math.max(displayCount, 0)),
    [allItems, displayCount],
  );

  const hasMore = displayCount < allItems.length;

  const loadMore = () => {
    setDisplayCount(prev => Math.min(prev + limit, allItems.length));
  };

  useEffect(() => {
    if (displayCount > limit) {
      loadMoreButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [displayCount, limit]);

  return {
    items,
    totalCount: allItems.length,
    hasMore,
    loadMore,
    loadMoreButtonRef,
    loading: isLoading,
    isLoadingMore: false,
    error,
    setError,
    networkStatus: isLoading ? NetworkStatus.loading : NetworkStatus.ready,
    refetch: async () => {
      return Promise.resolve({} as any);
    },
  };
};
